import { useNavigate, useLocation } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { useAuth } from "../context/AuthContext";
import { useEffect, useMemo, useState } from "react";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { toast } from "sonner";
import API from "../lib/api";
import auditLogo from "../assets/zentis-logo.png";
import AuditAssignment from "./AuditAssignment";
import AuditModeSelector from "./AuditModeSelector";
import {
  FileText, Upload, Target, Folder, BarChart3, Search,
  FileCheck, CheckSquare, FileOutput, Settings, Activity, Plus,
  Database, Shield, LogOut, Users, RefreshCw, UserCircle, PlugZap, Eye
} from "lucide-react";

// Strip HTML tags and script content from any string (safety measure)
const stripHtml = (str) => str ? str.replace(/<[^>]*>/g, '').replace(/&[a-z]+;/gi, '').trim() : '';

const getApiErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(detail) && detail.length) {
    return detail
      .map((item) => item?.msg || item?.message || JSON.stringify(item))
      .filter(Boolean)
      .join(", ");
  }
  if (typeof error?.response?.data?.message === "string" && error.response.data.message.trim()) {
    return error.response.data.message.trim();
  }
  if (error?.message === "Network Error" || !error?.response) {
    return `Backend unavailable at ${process.env.REACT_APP_BACKEND_URL || "the configured API URL"}`;
  }
  return error?.message || "Unknown error";
};

const NAV_ITEMS = [
  { phase: 1, label: "Context", icon: FileText, path: "/phase/1" },
  { phase: 2, label: "Documents", icon: Upload, path: "/phase/2" },
  { phase: 3, label: "Controls", icon: Shield, path: "/phase/3" },
  { phase: 4, label: "Audit Scope", icon: Target, path: "/phase/4" },
  { phase: 5, label: "Artifacts", icon: Folder, path: "/phase/5" },
  { phase: 6, label: "Sampling", icon: BarChart3, path: "/phase/6" },
  { phase: 7, label: "Evidence", icon: Search, path: "/phase/7" },
  { phase: "7-preview", label: "Evidence Preview", icon: Eye, path: "/phase/7-preview" },
  { phase: 8, label: "Findings", icon: FileCheck, path: "/phase/8" },
  { phase: 9, label: "Classification", icon: CheckSquare, path: "/phase/9" },
  { phase: 10, label: "Reports", icon: FileOutput, path: "/phase/10" },
];

const UTIL_ITEMS = [
  { label: "Integrations", icon: PlugZap, path: "/integrations" },
  { label: "Model Config", icon: Settings, path: "/config", permissions: ["model:config"] },
  { label: "User Management", icon: Users, path: "/users", permissions: ["users:manage"] },
  { label: "Audit Logs", icon: Activity, path: "/logs", permissions: ["logs:view"] },
];

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { auditRun, auditRuns, currentPhase, loadRun, fetchRuns } = useAudit();
  const { user: authUser, logout, hasAnyPermission } = useAuth();
  const canCreateRuns = hasAnyPermission("run:create");
  const canAssignRuns = hasAnyPermission("run:assign");
  const [newRunName, setNewRunName] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createStep, setCreateStep] = useState("name"); // "name" or "mode"
  const auditHealth = useMemo(() => {
    const score = Number(auditRun?.report?.stats?.compliance_score);
    if (Number.isFinite(score)) {
      if (score >= 80) return { label: "Healthy", tone: "good" };
      if (score >= 60) return { label: "Watch", tone: "warn" };
      return { label: "At Risk", tone: "risk" };
    }
    const highCrit = (auditRun?.report?.stats?.by_severity?.High || 0) + (auditRun?.report?.stats?.by_severity?.Critical || 0);
    if (highCrit >= 4) return { label: "At Risk", tone: "risk" };
    if (highCrit >= 2) return { label: "Watch", tone: "warn" };
    return { label: "Healthy", tone: "good" };
  }, [auditRun]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  const handleCreate = async () => {
    if (!newRunName.trim()) return;
    // Move to mode selection step
    setCreateStep("mode");
  };

  const handleModeSelect = async (modeConfig) => {
    try {
      // Create audit run with mode configuration
      const { data } = await API.post("/audit-runs", {
        name: newRunName.trim(),
        audit_mode: modeConfig.audit_mode,
        team_template: modeConfig.team_template
      });
      setNewRunName("");
      setDialogOpen(false);
      setCreateStep("name");
      await loadRun(data.id);
      await fetchRuns();
      navigate("/phase/1");
      toast.success(
        modeConfig.audit_mode === "team"
          ? `Audit team created with ${modeConfig.team_template} template!`
          : "Audit run created"
      );
    } catch (e) {
      const errorMessage = getApiErrorMessage(e);
      console.error("Failed to create audit run:", e);
      toast.error(`Failed to create audit run: ${errorMessage}`);
    }
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      setCreateStep("name");
      setNewRunName("");
    }
  };

  const handleSeedDemo = async () => {
    try {
      setDialogOpen(false);
      const { data } = await API.post("/demo/seed");
      await loadRun(data.audit_run_id);
      await fetchRuns();
      navigate("/phase/8");
      toast.success("Demo data loaded");
    } catch (e) {
      toast.error("Failed to seed demo data");
    }
  };

  const handleLoadRun = async (id) => {
    const data = await loadRun(id);
    navigate(`/phase/${data?.current_phase || 1}`);
  };

  const isPhaseAccessible = (phase) => {
    if (phase === "7-preview") {
      return Boolean(auditRun) && currentPhase >= 7;
    }
    if (!auditRun) return phase === 1;
    const completed = auditRun.phases_completed || [];
    return phase === 1 || completed.includes(phase - 1) || phase <= currentPhase;
  };

  return (
    <div className="audit-sidebar sidebar-enter h-screen border-r border-border text-foreground flex flex-col" data-testid="sidebar">
      {/* Logo */}
      <div className="audit-brand border-b border-border px-5 py-5">
        <div className="sidebar-logo-anim overflow-hidden rounded-2xl bg-background/40 p-2">
          <img
            src={auditLogo}
            alt="Zentis logo"
            className="block h-auto w-full object-contain"
          />
        </div>
      </div>

      {/* Audit Run Selector */}
      <div className="audit-panel space-y-3 border-b border-border p-3">
        <div className="flex gap-1">
          {canCreateRuns && (
            <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
              <DialogTrigger asChild>
                <Button size="sm" className="h-9 flex-1 border-0 bg-[#2563eb] text-xs text-white hover:bg-[#1d4ed8]" data-testid="new-audit-btn">
                  <Plus className="w-3 h-3 mr-1" /> New Audit
                </Button>
              </DialogTrigger>
              <DialogContent className={createStep === "mode" ? "max-w-xl max-h-[90vh] overflow-hidden" : ""}>
                <DialogHeader>
                  <DialogTitle>
                    {createStep === "name" ? "New Audit Run" : "Select Audit Mode"}
                  </DialogTitle>
                </DialogHeader>

                {createStep === "name" ? (
                  <div className="space-y-3 pt-2">
                    <Input
                      placeholder="Audit run name..."
                      value={newRunName}
                      onChange={e => setNewRunName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleCreate()}
                      autoFocus
                      data-testid="new-run-name-input"
                    />
                    <div className="flex gap-2">
                      <Button onClick={handleCreate} className="flex-1" data-testid="create-run-btn" disabled={!newRunName.trim()}>
                        Continue
                      </Button>
                      <Button variant="outline" onClick={handleSeedDemo} data-testid="seed-demo-btn">
                        <Database className="w-3 h-3 mr-1" /> Demo Data
                      </Button>
                    </div>
                  </div>
                ) : (
                  <AuditModeSelector
                    onSelect={handleModeSelect}
                    onCancel={() => setCreateStep("name")}
                  />
                )}
              </DialogContent>
            </Dialog>
          )}
          <Button
            size="sm"
            variant="outline"
            className={`h-9 border-border/70 bg-background/50 px-3 text-xs text-foreground hover:bg-accent ${!canCreateRuns ? 'flex-1' : ''}`}
            onClick={fetchRuns}
            data-testid="refresh-runs-list"
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            Refresh
          </Button>
          {canAssignRuns && auditRun && <AuditAssignment />}
        </div>
        {auditRun && (
          <p className="audit-active-audit truncate text-xs text-muted-foreground" title={auditRun.name}>
            <span className={`audit-health-pulse ${auditHealth.tone}`} />
            Active: <span className="text-foreground font-medium">{auditRun.name}</span>
            <span className="ml-1 text-[10px] text-muted-foreground">({auditHealth.label})</span>
          </p>
        )}
      </div>

      {/* Phase Navigation */}
      <ScrollArea className="flex-1">
        <div className="p-2 sidebar-stepper">
          <p className="label-xs mb-2 flex items-center gap-1 px-2 text-muted-foreground"><span className="section-dot" />Audit Journey</p>
          {NAV_ITEMS.map(item => {
            const active = location.pathname === item.path;
            const accessible = isPhaseAccessible(item.phase);
            const completed = auditRun?.phases_completed?.includes(item.phase);
            return (
              <button
                key={item.path}
                onClick={() => accessible && navigate(item.path)}
                disabled={!accessible}
                data-testid={`nav-phase-${item.phase}`}
                style={{ "--stagger": `${(typeof item.phase === "number" ? item.phase : 8) * 40}ms` }}
                className={`sidebar-nav-item quick-action w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-sm transition-all mb-1
                  ${active ? 'border border-primary/30 bg-primary/12 font-medium text-foreground' : 'text-muted-foreground'}
                  ${!accessible ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-accent'}
                  ${completed && !active ? 'text-foreground/80' : ''}
                  ${active ? 'active' : ''}
                `}
              >
                <item.icon className="nav-icon w-4 h-4 shrink-0" />
                <span className="truncate">{item.label}</span>
                {completed && <div className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" />}
              </button>
            );
          })}
        </div>
        <Separator className="my-1 bg-border" />
        <div className="p-2">
          <p className="label-xs mb-2 flex items-center gap-1 px-2 text-muted-foreground"><span className="section-dot" />Operations</p>
          {UTIL_ITEMS.filter(item => !item.permissions || hasAnyPermission(...item.permissions)).map(item => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              data-testid={`nav-${item.path.slice(1)}`}
              style={{ "--stagger": "420ms" }}
              className={`sidebar-nav-item quick-action w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-sm transition-all mb-1
                ${location.pathname === item.path ? 'bg-primary/10 text-foreground font-medium' : 'text-muted-foreground hover:bg-accent'}
              `}
            >
              <item.icon className="nav-icon w-4 h-4 shrink-0" />
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      </ScrollArea>

      {/* User Info & Logout */}
      <div className="space-y-2 border-t border-border bg-background/40 p-3">
        {authUser && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-2 rounded-xl px-1 py-1 text-left transition-colors hover:bg-accent/70"
              onClick={() => navigate("/profile")}
              data-testid="open-profile-btn"
            >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15">
              <span className="text-xs font-semibold text-foreground">{stripHtml(authUser.name)?.replace(/[^a-zA-Z0-9]/g, '')?.charAt(0)?.toUpperCase() || '?'}</span>
            </div>
            <div className="flex-1 min-w-0" title={stripHtml(authUser.name)}>
              <p className="text-sm font-medium truncate text-foreground">{stripHtml(authUser.name)}</p>
              <p className="truncate text-[11px] text-muted-foreground">
                {authUser.role === 'super_admin'
                  ? 'Super Admin'
                  : authUser.role === 'audit_manager'
                    ? 'Audit Manager'
                    : authUser.role === 'senior_auditor'
                      ? 'Senior Auditor'
                      : authUser.role === 'external_auditor'
                        ? 'External Auditor'
                        : authUser.role === 'control_owner'
                          ? 'Control Owner'
                          : authUser.role === 'observer'
                            ? 'Observer'
                            : 'Auditor'}
              </p>
            </div>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground">
              <UserCircle className="w-4 h-4" />
            </div>
            </button>
            <Button variant="ghost" size="sm" className="h-8 w-8 shrink-0 p-0 text-muted-foreground hover:bg-accent hover:text-foreground" onClick={() => { logout(); navigate('/login'); }} data-testid="logout-btn" aria-label="Logout">
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
        <p className="font-mono text-[10px] text-muted-foreground">v1.0.0-demo</p>
      </div>
    </div>
  );
}
